/*
  Quick view: run the injected markup's own scripts
  ------------------------------------------------------------------
  The quick-view modal builds its body by assigning HTML into
  [data-quick-view-contents]. Two rules of the platform bite here:

    1. A <script> inserted through innerHTML is parsed but NEVER executed.
       The spec marks such elements "already started" precisely so that
       assigning markup cannot run code.

    2. Even if it did run, most of the product-main blocks wrap their setup in
       document.addEventListener('DOMContentLoaded', ...). That event fired
       long before the shopper clicked anything, and it never fires twice, so
       the callback would sit there forever.

  Between them, every block that ships its own script — the variant selector,
  the price/discount updater, the custom add-to-cart, the thumbnail switcher —
  rendered as dead markup inside the modal. The controls appeared, and nothing
  responded to them.

  The theme's OWN components are fine: they are custom elements, and the
  browser upgrades those automatically when the nodes enter the document.
  Only the hand-written inline scripts needed help.

  This file is deliberately separate from the theme's bundles, which are
  encrypted and must not be edited.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-quick-view-contents]';
  var DONE = 'data-qv-executed';

  /*
    Re-run one script by replacing it with a fresh element. Copying the
    attributes matters: type, src and any data-* the block reads back off its
    own tag are all part of how these snippets behave.
  */
  function reExecute(oldScript) {
    if (oldScript.hasAttribute(DONE)) return;

    var type = (oldScript.getAttribute('type') || '').toLowerCase();
    /* Skip data blocks — JSON-LD, importmaps, templates. Executing those is
       meaningless and, for an importmap, throws. */
    if (type && type !== 'text/javascript' && type !== 'module' && type !== 'application/javascript') {
      oldScript.setAttribute(DONE, '');
      return;
    }

    var fresh = document.createElement('script');
    for (var i = 0; i < oldScript.attributes.length; i++) {
      var a = oldScript.attributes[i];
      fresh.setAttribute(a.name, a.value);
    }
    fresh.setAttribute(DONE, '');
    if (!oldScript.src) fresh.textContent = oldScript.textContent;

    oldScript.setAttribute(DONE, '');
    oldScript.parentNode.replaceChild(fresh, oldScript);
  }

  /*
    While the injected scripts run, a DOMContentLoaded registration means "set
    yourself up now" — the markup it is waiting for is already in the document.
    The patch is held only for the duration of the synchronous execution below,
    so nothing else in the page is affected.
  */
  function withImmediateReady(fn) {
    var original = document.addEventListener;

    document.addEventListener = function (type, listener, options) {
      if (type === 'DOMContentLoaded' && typeof listener === 'function') {
        try {
          listener.call(document, new Event('DOMContentLoaded'));
        } catch (err) {
          console.error('[quick-view] block setup failed:', err);
        }
        return;
      }
      return original.call(document, type, listener, options);
    };

    try {
      fn();
    } finally {
      document.addEventListener = original;
    }
  }

  function hydrate(container) {
    var scripts = container.querySelectorAll('script:not([' + DONE + '])');
    if (!scripts.length) return;

    withImmediateReady(function () {
      /* Static list: replaceChild mutates the live NodeList as we go. */
      Array.prototype.slice.call(scripts).forEach(reExecute);
    });

    /*
      Several blocks only update themselves in response to variant:change and
      render their initial state from Liquid. That initial state is correct as
      injected, so nothing is dispatched here — firing a synthetic change with
      a guessed variant would overwrite good markup with a wrong price.
    */
  }

  function watch(container) {
    if (container.__qvWatched) return;
    container.__qvWatched = true;

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) {
          hydrate(container);
          return;
        }
      }
    }).observe(container, { childList: true, subtree: true });

    /* Content may already be present if the modal was built before this ran. */
    hydrate(container);
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(watch);

    /*
      The modal itself is rendered by a snippet near the end of <body>, but a
      section could add another one later. Watching the body for new
      quick-view containers keeps this working without assuming load order.
    */
    new MutationObserver(function () {
      document.querySelectorAll(SELECTOR).forEach(watch);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
